import React, { useState } from 'react'
import styled from 'styled-components'
import copy from 'copy-to-clipboard'
import Button, { IconButton } from 'components/Button'
import { Flex } from 'components/Box'
import { ReactComponent as CopyIcon } from 'icons/squares.2.dotted.svg'
import { Placement } from '@popperjs/core'
import Tooltip from 'components/Tooltip'

export const ANIMATION_DURATION_IN_MILLISECONDS = 1500

interface IconProps {
  handleClick: () => void
  dropdown?: React.ReactNode
}

const Icon: React.FC<IconProps> = ({ handleClick, dropdown }) => (
  <Flex
    alignItems="center"
    maxHeight={17}
    maxWidth={17}
    padding="5px"
    marginTop="2px">
    <IconButton
      type="button"
      icon={CopyIcon}
      size="17px"
      aria-label="Copy"
      title="Copy"
      onClick={handleClick}
    />
    {dropdown}
  </Flex>
)

const CopyButton = styled(Button)`
  min-height: 40px;
  font-size: ${({ theme }) => theme.fontSizes.smaller};
`

const CopyOnLabelIconWrapper = styled.div`
  opacity: 0;
`

const CopyOnLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  cursor: pointer;

  &:hover {
    ${CopyOnLabelIconWrapper} {
      opacity: 1;
    }
  }
`

interface CopyProps {
  title?: string
  onCopy?: (value: string) => void
  value?: string
  placement?: Placement
  spacing?: string
  dropdown?: React.ReactNode
  className?: string
  showIcon?: boolean
  disabled?: boolean
  fullWidth?: boolean
}

const CopyToClipboard: React.FC<CopyProps> = ({
  title = 'Copy',
  onCopy = () => {},
  placement = 'left',
  spacing = '10px',
  value = '',
  dropdown,
  showIcon = true,
  children,
  disabled,
  fullWidth,
  className,
}) => {
  const defaultTooltip = 'Copy to clipboard'
  const [copyTooltipText, setCopyTooltipText] = useState(defaultTooltip)
  const [tooltipVisible, setTooltipVisible] = useState(false)

  const handleClick = () => {
    if (disabled) {
      return
    }

    setCopyTooltipText('Copied')
    setTooltipVisible(true)

    if (typeof value === 'string' || typeof value === 'number') {
      copy(`${value}`)
      onCopy(`${value}`)
    }

    window.setTimeout(() => {
      setTooltipVisible(false)
      setCopyTooltipText(defaultTooltip)
    }, ANIMATION_DURATION_IN_MILLISECONDS)
  }

  const handleMouseEnter = () => setTooltipVisible(true)
  const handleMouseLeave = () => setTooltipVisible(false)

  const getPopperContent = () => {
    if (dropdown || fullWidth) {
      return children ? (
        <CopyOnLabelContainer
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          {children}
          <CopyOnLabelIconWrapper>
            {showIcon ? (
              <Icon handleClick={handleClick} dropdown={dropdown} />
            ) : (
              dropdown
            )}
          </CopyOnLabelIconWrapper>
        </CopyOnLabelContainer>
      ) : (
        <Icon handleClick={handleClick} dropdown={dropdown} />
      )
    }

    if (showIcon) {
      return (
        <IconButton
          type="button"
          icon={CopyIcon}
          size="17px"
          aria-label={title}
          title={title}
          onClick={handleClick}>
          &nbsp;{title}
        </IconButton>
      )
    }

    return (
      <CopyButton
        className={className}
        type="button"
        aria-label={title}
        onClick={handleClick}>
        {title}
      </CopyButton>
    )
  }

  return (
    <Tooltip
      className="CopyToClipboard"
      visible={tooltipVisible}
      placement={placement}
      spacing={spacing}
      content={copyTooltipText}>
      {getPopperContent()}
    </Tooltip>
  )
}

export default CopyToClipboard
